/**
 * SortRow Component
 *
 * Single sort row with drag handle, field selector, direction toggle, and remove button.
 * Represents one sort field in the multi-field sorting configuration.
 */

import { Select, Button, Tooltip, Spin, Alert } from 'antd'
import { HolderOutlined, ArrowUpOutlined, ArrowDownOutlined, CloseOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { metadataService, type FieldMetadata } from '@/services/metadataService'
import type { SortField } from '../hooks/useSorting'

export interface SortRowProps {
  /** Current sort field data */
  sort: SortField

  /** Entity model name */
  model: string

  /** Pre-fetched field metadata (avoids metadata API calls) */
  fields?: Record<string, FieldMetadata>

  /** Callback when sort field changes */
  onChange: (updates: Partial<Omit<SortField, 'id'>>) => void

  /** Callback when sort field is removed */
  onRemove: () => void

  /** Index in the list (for priority display) */
  index: number

  /** Drag handle props (from dnd-kit) */
  dragHandleProps?: any
}

export function SortRow({
  sort,
  model,
  fields: passedFields,
  onChange,
  onRemove,
  index,
  dragHandleProps,
}: SortRowProps) {
  // Only fetch field metadata if not passed from parent
  const { data: metadataResponse, isLoading: isMetadataLoading } = useQuery({
    queryKey: ['metadata', model, 'fields'],
    queryFn: async () => {
      const response = await metadataService.getNonSystemFields(model)
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch metadata')
      }
      return response.data
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    // Skip fetch if fields are passed from parent
    enabled: !passedFields,
  })

  // Use passed fields or fetched fields
  const fields = passedFields ?? metadataResponse?.fields ?? {}
  const formSections = metadataResponse?.form_sections ?? []
  const isLoading = passedFields ? false : isMetadataLoading

  // Build field options grouped by form section
  const fieldOptions = Object.entries(fields)
    .filter(([_, fieldMeta]) => fieldMeta.is_visible !== false)
    .map(([key, fieldMeta]) => ({
      label: fieldMeta.title || key,
      value: key,
      formSection: fieldMeta.form_section,
    }))

  // Group by form section
  const groupedOptions = formSections.map((section) => ({
    label: section.name,
    options: fieldOptions.filter((opt) => opt.formSection === section.name),
  }))

  // Add ungrouped fields
  const ungroupedFields = fieldOptions.filter(
    (opt) => !opt.formSection || !formSections.find((s) => s.name === opt.formSection)
  )
  if (ungroupedFields.length > 0) {
    groupedOptions.push({
      label: 'Other Fields',
      options: ungroupedFields,
    })
  }

  // Handle field change
  const handleFieldChange = (newKey: string) => {
    onChange({ key: newKey })
  }

  // Handle direction toggle
  const handleDirectionToggle = () => {
    onChange({ direction: sort.direction === 'asc' ? 'desc' : 'asc' })
  }

  // Priority label (1st, 2nd, 3rd, etc.)
  const priorityLabel = ['1st', '2nd', '3rd', '4th', '5th'][index] || `${index + 1}th`

  // Validate sort field
  const hasError = !sort.key
  const validationError = hasError ? 'Please select a field to sort by' : null

  // Show loading spinner while metadata loads
  if (isLoading) {
    return (
      <div
        style={{
          width: '100%',
          marginBottom: 8,
          padding: '8px',
          backgroundColor: '#fafafa',
          borderRadius: '4px',
          border: '1px solid #d9d9d9',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Spin size="small" />
        <span style={{ color: '#8c8c8c' }}>Loading fields...</span>
      </div>
    )
  }

  return (
    <div style={{ width: '100%', marginBottom: 8 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto auto',
          gap: '8px',
          alignItems: 'center',
          width: '100%',
          padding: '8px',
          backgroundColor: hasError ? '#fff2f0' : '#fafafa',
          borderRadius: '4px',
          border: hasError ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
        }}
      >
        {/* Drag Handle - auto width */}
        <Tooltip title={`${priorityLabel} sort priority (drag to reorder)`}>
          <div
            {...dragHandleProps}
            style={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              padding: '4px',
              color: '#8c8c8c',
            }}
          >
            <HolderOutlined style={{ fontSize: 16 }} />
          </div>
        </Tooltip>

        {/* Field Selector - 1fr fills row */}
        <Select
          placeholder="Select field to sort"
          value={sort.key || undefined}
          onChange={handleFieldChange}
          showSearch
          filterOption={(input, option) =>
            (option?.label?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
          }
          options={groupedOptions}
          status={!sort.key ? 'error' : undefined}
        />

        {/* Direction Toggle - auto width, wider for full words */}
        <Tooltip title={`Sort ${sort.direction === 'asc' ? 'ascending' : 'descending'} (click to toggle)`}>
          <Button
            icon={sort.direction === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            onClick={handleDirectionToggle}
            disabled={!sort.key}
            style={{ minWidth: 110 }}
          >
            {sort.direction === 'asc' ? 'Ascending' : 'Descending'}
          </Button>
        </Tooltip>

        {/* Remove Button - auto width */}
        <Tooltip title="Remove sort field">
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onRemove}
            danger
          />
        </Tooltip>
      </div>

      {/* Error Message */}
      {hasError && (
        <Alert
          message={validationError}
          type="error"
          showIcon
          style={{ marginTop: 4 }}
          closable={false}
        />
      )}
    </div>
  )
}
